/**
 * Feeds the top-nav CRM alert — the red dot (and the periodic wave) on the CRM
 * tab when THIS operator has invoices past due.
 *
 * Three rules shape it:
 *
 *  1. **It is per-actor, not per-tenant.** The count comes from
 *     `/api/crm/invoices/overdue-summary`, which reads crm-api with this
 *     operator's own CRM token — own records unless they hold `crm:view_all`.
 *     A salesperson whose invoices are all current gets no dot even while the
 *     company has overdue invoices belonging to someone else. That is the whole
 *     feature: the dot means *you* have something to chase.
 *  2. **It never fetches for someone who may not see it.** Gated on `crm:view`
 *     (can open the CRM at all) AND `crm:financials` (the financial wall — an
 *     invoice being overdue is monetary information). A viewer missing either
 *     issues no request, so a 403 can't even be attempted.
 *  3. **A failed read is silent.** react-query keeps the last good value and
 *     retries on the next poll; an indicator must never turn a network blip
 *     into a false alarm — or into a false all-clear that flickers.
 *
 * It also listens for the CRM's own `crm:overdue-invoices-changed` broadcast,
 * so settling the last invoice in the CRM drawer clears the nav dot instantly
 * instead of up to five minutes later.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  hasPermission,
  overdueSeverity,
  type OverdueSeverity,
  type OverdueTotals,
} from "@xop/dashboard-shared";

import { useAuth } from "../../auth/AuthProvider.js";
import { api } from "../api.js";

/** Slow on purpose: an invoice does not fall overdue second to second, and this
 * runs for every signed-in operator on every page of the app. */
export const OVERDUE_BADGE_POLL_MS = 5 * 60 * 1000;

export const overdueBadgeKey = ["crm", "overdue-summary"] as const;

/** The CRM's cross-surface "a payment landed" broadcast
 * (`crm/lib/overdue-invoices-api.ts`). Duplicated as a string rather than
 * imported because the app shell must not pull the walled CRM sub-app into its
 * bundle — the CRM owns the event, this is a listener. */
export const OVERDUE_INVOICES_CHANGED_EVENT = "crm:overdue-invoices-changed";

export interface OverdueBadge {
  /** True only when there is something to chase AND this viewer may see it. */
  active: boolean;
  totals: OverdueTotals;
  severity: OverdueSeverity;
}

const EMPTY: OverdueTotals = { count: 0, totalAmount: 0, maxDaysOverdue: 0 };

export function useOverdueInvoiceBadge(): OverdueBadge {
  const { user } = useAuth();
  const qc = useQueryClient();
  // Both gates: the CRM entry gate and the financial wall.
  const enabled =
    hasPermission(user?.permissions, "crm:view") &&
    hasPermission(user?.permissions, "crm:financials");

  const query = useQuery({
    queryKey: overdueBadgeKey,
    enabled,
    queryFn: () => api<OverdueTotals>("/api/crm/invoices/overdue-summary"),
    refetchInterval: OVERDUE_BADGE_POLL_MS,
    refetchOnWindowFocus: true,
    // Keep the last good count on a failed poll — see rule 3.
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!enabled) return;
    const onChanged = () => {
      void qc.invalidateQueries({ queryKey: overdueBadgeKey });
    };
    window.addEventListener(OVERDUE_INVOICES_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(OVERDUE_INVOICES_CHANGED_EVENT, onChanged);
  }, [enabled, qc]);

  const totals = enabled ? (query.data ?? EMPTY) : EMPTY;
  return {
    active: totals.count > 0,
    totals,
    severity: overdueSeverity(totals),
  };
}
